// Identity, accounts and access (0022): command arguments and the People & access read.
// Access grants are software capabilities; signing authority is a licence (0021), never a grant.
import type { Iso, Uuid } from './types.ts'

/** The persistent strip and print note of a staging (verification) environment (0022). */
export const STAGING_NOTICE = 'STAGING - test records, not a permit to work. Use it to verify the system; never to authorise real work.'

export const SITE_CAPABILITIES = ['view_site', 'apply_permits', 'record_evidence'] as const
export const GLOBAL_CAPABILITIES = ['administer_accounts', 'administer_registry', 'administer_policy'] as const
export type SiteCapability = (typeof SITE_CAPABILITIES)[number]
export type GlobalCapability = (typeof GLOBAL_CAPABILITIES)[number]
export type Capability = SiteCapability | GlobalCapability

export const CAPABILITY_LABELS: Record<Capability, { label: string; help: string }> = {
  view_site: { label: 'View permits', help: 'See the permits, board and records of the site.' },
  apply_permits: { label: 'Apply for permits', help: 'Start and submit permit applications at the site (a PA licence is still needed to submit).' },
  record_evidence: { label: 'Record evidence', help: 'Add supporting documents and evidence to permits at the site.' },
  administer_accounts: { label: 'Account administrator', help: 'Invite people, manage accounts and access, keep the licence register. Not a signing authority.' },
  administer_registry: { label: 'Registry administrator', help: 'Maintain sites, units and equipment. Not a signing authority.' },
  administer_policy: { label: 'Rulebook administrator', help: 'Prepare, approve the record of and activate the BadrEOR PTW rulebook (policy versions). Not a signing authority.' },
}

export type GrantRequest = { capability: Capability; site_id?: Uuid | null }

/** Commands of 0022 and 0026 (public.ptw_command). */
export type IdentityCommandArgs = {
  add_person: { display_name: string; employer_organisation_id: Uuid; staff_reference?: string | null; note?: string | null }
  invite_person: { person_id: Uuid; email: string; grants: GrantRequest[]; valid_days?: number; note?: string | null }
  resend_invitation: { invitation_id: Uuid; valid_days?: number }
  expire_invitation: { invitation_id: Uuid; reason: string }
  suspend_account: { account_id: Uuid; reason: string }
  reactivate_account: { account_id: Uuid; reason: string }
  disable_account: { account_id: Uuid; reason: string }
}

/** manage_access_grant arguments (the command is declared in CommandArgs). */
export type ManageAccessGrantArgs = {
  action: 'grant' | 'revoke'
  account_id: Uuid
  capability: Capability
  site_id?: Uuid | null
  reason: string
}

export type AccountStatus = 'pending' | 'active' | 'suspended' | 'disabled'
export type InvitationStatus = 'pending' | 'expired' | 'accepted' | 'superseded'

export type InvitationView = {
  id: Uuid
  email: string
  status: InvitationStatus
  created_at: Iso
  expires_at: Iso
  invited_by: string | null
  invited_by_kind: 'account' | 'provisioning'
  supersedes_invitation_id: Uuid | null
  note: string | null
  closed_at: Iso | null
  closure_reason: string | null
  grants: { capability: Capability; site_id: Uuid | null; site_name: string | null }[]
}

export type AdminPerson = {
  person_id: Uuid
  display_name: string
  organisation: string | null
  staff_reference: string | null
  is_synthetic: boolean
  is_self: boolean
  account: {
    id: Uuid
    status: AccountStatus
    email: string | null
    created_at: Iso
    disabled_at: Iso | null
    disabled_reason: string | null
  } | null
  grants: {
    id: Uuid
    capability: Capability
    site_id: Uuid | null
    site_name: string | null
    site_code: string | null
    granted_at: Iso
    reason: string
    granted_by: string | null
    inherited: boolean
  }[]
  invitations: InvitationView[]
  history: {
    account_id: Uuid
    from_status: AccountStatus | null
    to_status: AccountStatus
    reason: string
    changed_at: Iso
    changed_by_kind: 'account' | 'self_activation'
    changed_by: string | null
  }[]
}

export type AccountAdminView = {
  generated_at: Iso
  environment_kind: string
  invitations_enabled: boolean
  self_person_id: Uuid
  site_capabilities: SiteCapability[]
  global_capabilities: GlobalCapability[]
  sites: { id: Uuid; code: string; name: string }[]
  /** The organisations the administrator may add people to (0026), the company first. */
  organisations?: { id: Uuid; code: string; name: string; kind: 'company' | 'client' | 'subcontractor' | 'authority' }[]
  people: AdminPerson[]
}
